import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './QuantitySelector.module.css';

const getQueryParamName = queryParamNames =>
  Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;

const parseRangeString = value => {
  if (!value || typeof value !== 'string') return null;

  const parts = value.split(',');
  if (parts.length !== 2) return null;

  const start = Number(parts[0]);
  const end = Number(parts[1]);

  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
};

/**
 * MIN -> MAX (inclusive) using exclusive end:
 * selected v => "v,(max+1)"
 * all/null => remove filter
 */
const format = (selectedMin, queryParamName, max) => {
  if (selectedMin == null) return { [queryParamName]: null };

  const v = Number(selectedMin);
  if (!Number.isFinite(v)) return { [queryParamName]: null };

  const exclusiveEnd = Number(max) + 1;
  return { [queryParamName]: `${v},${exclusiveEnd}` };
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const StepperField = props => {
  const { input, min, max, step = 1, ariaLabel, allLabel = 'all', label, formatValue } = props;
  const { value, onChange } = input;


  const isAll = value == null ||  value == 0 ;

  const shownValue = isAll
    ? allLabel
    : formatValue
      ? formatValue(value)
      : value;

  const dec = e => {
    e.preventDefault();
    e.stopPropagation();

    if (isAll) return;

    const next = value - step;

    if (next < min) onChange(null);
    else onChange(clamp(next, min, max));
  };

  const inc = e => {
    e.preventDefault();
    e.stopPropagation();

    if (isAll) {
      onChange(min);
      return;
    }

    const next = value + step;
    onChange(clamp(next, min, max));
  };

  const disableMinus = isAll;
  const disablePlus = !isAll && value >= max;

  return (
    <div className={css.container}>
        <p className={css.titleLabel}>{label}</p>
    <div className={css.row} aria-label={ariaLabel}>
      <button
        type="button"
        className={classNames(css.btn, css.btnMinus)}
        onClick={dec}
        disabled={disableMinus}
        aria-label="Decrease"
      >
        <span className={css.iconMinus} />
      </button>

      <span className={css.value} aria-live="polite">
        {shownValue}
      </span>

      <button
        type="button"
        className={classNames(css.btn, css.btnPlus)}
        onClick={inc}
        disabled={disablePlus}
        aria-label="Increase"
      >
        <span className={css.iconPlus} />
      </button>
    </div>
    </div>
  );
};

const QuantitySelector = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    queryParamNames,
    initialValues,
    onSubmit,
    min = 1,
    max = 10,
    step = 1,
    showAsPopup,
    contentPlacementOffset = 0,
    allLabel,
    formatValue,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const queryParamName = getQueryParamName(queryParamNames);

  const rawParam = initialValues?.[queryParamName];
  const parsed = rawParam ? parseRangeString(rawParam) : null;

  let selectedFromInitial = null;
  if (parsed) {
    const { start, end } = parsed;

    const exclusiveEnd = Number(max) + 1;

    const looksLikeMinToMax = end === exclusiveEnd;
    const looksLikeExactExclusive = end === start + 1;
    const looksLikeExactLegacy = end === start;

    if (looksLikeMinToMax || looksLikeExactExclusive || looksLikeExactLegacy) {
      selectedFromInitial = start;
    }
  }

  const isSelected = selectedFromInitial != null;
  const namedInitialValues = { [name]: selectedFromInitial ?? null };

  const handleSubmit = values => {
    const v = values ? values[name] : null;
    onSubmit(format(v, queryParamName, max));
  };

  const aria = getAriaLabel ? getAriaLabel(label, rawParam || '') : label;

  const allLabelText =
    allLabel != null ? allLabel : intl.formatMessage({ id: 'QuantitySelector.all' });

  const labelSelection = isSelected ? "" : "";

  const fieldNode = (
    <Field
      name={name}
      render={({ input }) => (
        <StepperField
          input={input}
          label={label}
          min={min}
          max={max}
          step={step}
          ariaLabel={aria}
          allLabel={allLabelText}
          formatValue={formatValue}
        />
      )}
    />
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={label}
      ariaLabel={aria}
      isSelected={isSelected}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      {fieldNode}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
   
      labelSelection={labelSelection}
      ariaLabel={aria}
      isSelected={isSelected}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      {fieldNode}
    </FilterPlain>
  );
};

export default QuantitySelector;
